import { hashKey, type QueryKey } from '@tanstack/react-query';

import { IndexedDBStorageAdapter } from './indexed-db-storage-adapter';

const DEFAULT_STORE_NAME = 'offline-default-data-store';
const DATABASE_NAME_PREFIX = 'infield-offline/';
export const OFFLINE_DATA_SCHEMA_VERSION = 1;

type OfflineStorageAdapter = Pick<
  IndexedDBStorageAdapter,
  'count' | 'get' | 'getPage' | 'set'
>;

export type CreateOfflineStorageAdapter = (
  indexedDbFactory: IDBFactory,
  databaseName: string,
  storeName: string
) => OfflineStorageAdapter;

const createIndexedDBStorageAdapter: CreateOfflineStorageAdapter = (
  indexedDbFactory,
  databaseName,
  storeName
) => new IndexedDBStorageAdapter(indexedDbFactory, databaseName, storeName);

export interface OfflineDataStoreScope {
  cluster: string;
  project: string;
  userId: string;
  location: string;
  locationSpace: string;
}

export type OfflineDataStoreIdentity = Pick<
  OfflineDataStoreScope,
  'cluster' | 'project' | 'userId'
>;

export interface OfflineDataPageOptions {
  limit: number;
  cursor?: string;
}

export type OfflineJsonValue =
  | null
  | boolean
  | number
  | string
  | OfflineJsonValue[]
  | object;

export interface OfflineDataPage {
  items: OfflineJsonValue[];
  nextCursor?: string;
}

export const getKey = (queryKey: QueryKey) => {
  return `v${OFFLINE_DATA_SCHEMA_VERSION}:${hashKey(queryKey)}`;
};

const encodeScopeParts = (parts: string[]) =>
  parts.map((part) => encodeURIComponent(part)).join('/');

/**
 * Prefix shared by every database of a user within a project, across all locations.
 * The trailing slash prevents a user id from matching another id that starts with it.
 */
export const getUserProjectDbNamePrefix = ({
  cluster,
  project,
  userId,
}: OfflineDataStoreIdentity) =>
  `${DATABASE_NAME_PREFIX}${encodeScopeParts([cluster, project, userId])}/`;

// Built on the user/project prefix so the wipe always matches the names created here.
export const getDatabaseName = (scope: OfflineDataStoreScope) =>
  `${getUserProjectDbNamePrefix(scope)}${encodeScopeParts([
    scope.location,
    scope.locationSpace,
  ])}`;

/** The inverse of `getDatabaseName`. Returns `undefined` for any other database name. */
export const parseDatabaseName = (
  name: string
): OfflineDataStoreScope | undefined => {
  if (!name.startsWith(DATABASE_NAME_PREFIX)) return undefined;

  const parts = name.slice(DATABASE_NAME_PREFIX.length).split('/');
  if (parts.length !== 5) return undefined;

  try {
    const [cluster, project, userId, location, locationSpace] = parts.map(
      (part) => decodeURIComponent(part)
    );
    const scope = { cluster, project, userId, location, locationSpace };
    // A name that `getDatabaseName` would not build is not a scope of this store.
    const isScopeName =
      getDatabaseName(scope) === name &&
      Object.values(scope).every((part) => part.trim());
    return isScopeName ? scope : undefined;
  } catch {
    // `decodeURIComponent` throws on a malformed escape sequence.
    return undefined;
  }
};

export type OfflineDataStore = ReturnType<typeof createOfflineDataStore>;

export function createOfflineDataStore(
  scope: OfflineDataStoreScope,
  storeName: string = DEFAULT_STORE_NAME,
  indexedDbFactory: IDBFactory = indexedDB,
  createStorageAdapter: CreateOfflineStorageAdapter = createIndexedDBStorageAdapter
) {
  const { cluster, project, userId, location, locationSpace } = scope;
  if (
    !cluster.trim() ||
    !project.trim() ||
    !userId.trim() ||
    !location.trim() ||
    !locationSpace.trim()
  ) {
    throw new Error(
      'Offline data storage requires a cluster, project, user, location, and location space'
    );
  }

  const databaseName = getDatabaseName(scope);

  const offlineStore = createStorageAdapter(
    indexedDbFactory,
    databaseName,
    storeName
  );

  return {
    storeIdentity: {
      databaseName,
      storeName,
    },

    /** Data must be JSON-compatible. Resolves after the payload is stored. */
    async save(queryKey: QueryKey, data: unknown): Promise<void> {
      const key = getKey(queryKey);

      let serialized: string;
      try {
        const result = JSON.stringify(data);
        if (result === undefined) {
          throw new Error('Offline data must be JSON-compatible');
        }
        serialized = result;
      } catch (error) {
        throw new Error('Offline data must be JSON-compatible', {
          cause: error,
        });
      }

      await offlineStore.set(key, serialized);
    },

    /** Reads one payload. The caller validates its application-specific shape. */
    async get(queryKey: QueryKey): Promise<OfflineJsonValue | undefined> {
      const key = getKey(queryKey);
      const serialized = await offlineStore.get(key);
      if (serialized === null) return undefined;

      const data: OfflineJsonValue = JSON.parse(serialized);
      return data;
    },

    count(): Promise<number> {
      return offlineStore.count();
    },

    /**
     * Reads payloads in stable key order. Pass nextCursor to continue with the next page.
     * Malformed stored JSON is skipped, so items may be empty while nextCursor is present;
     */
    async getAll({
      limit,
      cursor,
    }: OfflineDataPageOptions): Promise<OfflineDataPage> {
      const { values, nextCursor } = await offlineStore.getPage(limit, cursor);

      // Values are always stored using JSON.stringify, so only JSON values are expected in the database.
      // If future storage supports other formats, the logic for parsing and validation should be updated accordingly.
      const items = values.reduce<OfflineJsonValue[]>((acc, value) => {
        try {
          const parsedValue: OfflineJsonValue = JSON.parse(value);
          acc.push(parsedValue);
        } catch {
          // TODO(https://cognitedata.atlassian.net/browse/FO-4315): Add it to telemetry/instrumentation (for observability/monitoring purposes)
        }
        return acc;
      }, []);

      return { items, nextCursor };
    },
  };
}
